import React, { useState, useMemo } from "react";
import comparatifData from "../data/comparatifData";
import networkData from "../data/networkData";
import { FaMedal, FaInfoCircle, FaChartBar } from "react-icons/fa";
import { motion } from "framer-motion";
import ComparativeChartModal from '../components/ComparativeChartModal';
import ComparativeChart from '../components/ComparativeChart';

const OffersPage = () => {
  const [selectedType, setSelectedType] = useState("Tous");
  const [showInfo, setShowInfo] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("Appels");

  // Grouper les offres par type
  const groupedOffers = useMemo(() => {
    return comparatifData.reduce((acc, offer) => {
      const network = networkData.find((n) => n.id === offer.networkId);
      if (!acc[offer.type]) {
        acc[offer.type] = {};
      }
      if (!acc[offer.type][network.name]) {
        acc[offer.type][network.name] = [];
      }
      acc[offer.type][network.name].push(offer);
      return acc;
    }, {});
  }, []);

  const offerTypes = ["Tous", ...Object.keys(groupedOffers)];

  const filteredOffers =
    selectedType === "Tous"
      ? groupedOffers
      : { [selectedType]: groupedOffers[selectedType] };

  const getNetworkColor = (networkName) => {
    const colors = {
      Orange: "orange",
      MTN: "yellow",
      Moov: "blue",
    };
    return colors[networkName] || "gray";
  };

  const getBestOffer = (offers) => {
    return Object.values(offers)
      .flat()
      .reduce((best, current) =>
        parseFloat(current.description.match(/\d+/)) >
        parseFloat(best.description.match(/\d+/))
          ? current
          : best
      );
  };

  const getBestNetwork = (offers) => {
    const networkScores = Object.entries(offers).reduce(
      (acc, [network, offerList]) => {
        acc[network] = offerList.filter(
          (offer) => offer === getBestOffer(offers)
        ).length;
        return acc;
      },
      {}
    );
    return Object.entries(networkScores).reduce((a, b) =>
      a[1] > b[1] ? a : b
    )[0];
  };

  // Préparer les données pour le graphique
  const chartData = useMemo(() => {
    const categories = ['Appels', 'Internet', 'SMS'];
    return networkData.map(network => {
      const networkOffers = comparatifData.filter(offer => offer.networkId === network.id);
      const categoryScores = categories.map(category => {
        const relevantOffers = networkOffers.filter(offer => offer.type.toLowerCase().includes(category.toLowerCase()));
        const score = relevantOffers.reduce((acc, offer) => {
          const value = parseFloat(offer.description.match(/\d+/)) || 0;
          return acc + value;
        }, 0);
        return { category, score };
      });
      return {
        operator: network.name,
        scores: categoryScores
      };
    });
  }, [comparatifData, networkData]);

  return (
    <div className="container lg:px-20 mx-auto px-4 py-8">
      <motion.h1
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-4xl font-bold mb-12 text-center"
      >
        Comparatif des Offres
      </motion.h1>

      {/* Bouton pour les graphiques comparatifs */}
      <div className="mb-6 flex justify-center">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-black text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:bg-gray-800 transition-colors duration-200"
          onClick={() => setIsChartModalOpen(true)}
        >
          <FaChartBar className="mr-2" />
          Voir les graphiques comparatifs
        </motion.button>
      </div>

      <div className="mb-8 flex justify-center items-center">
        <div className="flex flex-wrap justify-center items-center space-x-2 space-y-2">
          {offerTypes.map((type) => (
            <motion.button
              key={type}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`border rounded-lg px-4 py-2 mt-2 focus:outline-none focus:ring-2 focus:ring-orange-500 transition duration-200 ${
                selectedType === type
                  ? "bg-orange-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
              style={{ height: "40px" }}
              onClick={() => setSelectedType(type)}
            >
              {type}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Icône de notification déplacée ici */}
      <div className="flex justify-center mb-4">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="text-gray-600 hover:text-gray-800"
          onClick={() => setShowInfo(!showInfo)}
        >
          <FaInfoCircle size={24} />
        </motion.button>
      </div>

       {/* {showInfo && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-8 rounded"
        >
          <p className="font-bold">Comment lire ce comparatif ?</p>
          <p>Les offres sont comparées par type. La meilleure offre de chaque catégorie est surlignée en vert. Le réseau avec le plus d'offres avantageuses est indiqué en haut de chaque section.</p>
        </motion.div>
      )} */}

      {showInfo && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-8 rounded"
        >
          <p className="font-bold">Important : Données de simulation</p>
          <p>
            Les informations actuellement affichées sont des données de
            simulation et ne reflètent pas les offres réelles des opérateurs.
          </p>
          <p className="mt-2">
            Nous travaillons activement pour intégrer les données exactes des
            offres de chaque opérateur. Ces informations précises seront
            disponibles très prochainement.
          </p>
          <p className="mt-6">
            Nous vous remercions de votre patience et de votre compréhension
            pendant cette phase de finalisation.
          </p>
        </motion.div>
      )}

      {Object.entries(filteredOffers).map(([type, networks]) => {
        const bestOffer = getBestOffer(networks);
        const bestNetwork = getBestNetwork(networks);
        return (
          <motion.div
            key={type}
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-semibold mb-4">{type}</h2>
            <div className="bg-gray-100 p-4 rounded-lg mb-4 flex items-center">
              <FaMedal className="text-yellow-500 mr-2" size={24} />
              <span className="font-semibold">
                Meilleur réseau pour cette catégorie : {bestNetwork}
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg">
              <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                <thead className="bg-black text-white">
                  <tr>
                    <th className="border border-gray-300 p-3 text-left">
                      Opérateur
                    </th>
                    <th className="border border-gray-300 p-3 text-left">
                      Offre
                    </th>
                    <th className="border border-gray-300 p-3 text-left">
                      Description
                    </th>
                    <th className="border border-gray-300 p-3 text-left">
                      Code
                    </th>
                    <th className="border border-gray-300 p-3 text-left">
                      Validité
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(networks).map(([networkName, offers]) =>
                    offers.map((offer) => (
                      <motion.tr
                        key={offer.id}
                        className={
                          offer === bestOffer
                            ? "bg-yellow-100"
                            : "hover:bg-gray-100"
                        }
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.2 }}
                      >
                        <td
                          className={`border border-gray-300 p-3 bg-${getNetworkColor(
                            networkName
                          )}-100`}
                        >
                          {networkName}
                        </td>
                        <td className="border border-gray-300 p-3">
                          {offer.title}
                        </td>
                        <td className="border border-gray-300 p-3">
                          {offer.description}
                        </td>
                        <td className="border border-gray-300 p-3 font-mono">
                          {offer.code}
                        </td>
                        <td className="border border-gray-300 p-3">
                          {offer.validUntil}
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <p>* La meilleure offre est surlignée en jaune.</p>
            </div>
          </motion.div>
        );
      })}

      <ComparativeChartModal 
        isOpen={isChartModalOpen} 
        onClose={() => setIsChartModalOpen(false)}
      >
        <h2 className="text-2xl font-bold text-center mb-6">Graphiques comparatifs des offres</h2>
        <div className="mb-4 flex justify-center space-x-4">
          {['Appels', 'Internet', 'SMS'].map(category => (
            <button
              key={category}
              className={`px-4 py-2 rounded ${
                selectedCategory === category 
                  ? 'bg-black text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        <ComparativeChart data={chartData} category={selectedCategory} />
      </ComparativeChartModal>
    </div>
  );
};

export default OffersPage;